import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, RotateCcw, Plus, Building, CheckCircle, Users, Database, Zap, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { solarAPI } from '../../services/api';
import { SolarPanel } from '../../types';
import { Alert } from '../common/Alert';
import { Input } from '../common/Input';
import { Button } from '../common/Button';
import { SearchResults } from './SearchResult';
import { PanelRequestForm } from './PanelRequestForm';
import { SupplierRequestForm } from '../Supplier/SupplierRequestForm';
import { SupplierRequestSuccess } from '../Supplier/SupplierRequestSuccess';
import { isAuthenticated } from '../../utils/auth';

interface FilterConfig {
  value: string;
  tolerance: string;
  toleranceType: 'percent' | 'units';
  operator?: 'equals' | 'gt' | 'lt' | 'gte' | 'lte';
}

export const SearchPanels: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Record<string, FilterConfig>>({});
  const [results, setResults] = useState<SolarPanel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [cellTypes, setCellTypes] = useState<string[]>([]);
  const [requestPanelOpen, setRequestPanelOpen] = useState(false);
  const [supplierRequestOpen, setSupplierRequestOpen] = useState(false);
  const [supplierRequestSuccess, setSupplierRequestSuccess] = useState(false);
  const [generalSearch, setGeneralSearch] = useState('');
  const [searchLimit, setSearchLimit] = useState<{
    maxSearches: number;
    remainingSearches: number;
    limitReached: boolean;
  } | null>(null);

  // Restore search state after login
  const [hasRestored, setHasRestored] = useState(false);
  const [showRestoredMessage, setShowRestoredMessage] = useState(false);
  
  useEffect(() => {
    solarAPI.getCellTypes().then(setCellTypes);
    
    // Check if we need to restore search state
    const searchState = sessionStorage.getItem('searchState');
    if (searchState && !hasRestored) {
      try {
        const state = JSON.parse(searchState);
        
        // Restore state
        if (state.generalSearch) {
          setGeneralSearch(state.generalSearch);
        }
        if (state.filters) {
          setFilters(state.filters);
        }
        
        // Mark as restored
        setHasRestored(true);
        
        // Clear sessionStorage
        sessionStorage.removeItem('searchState');
        sessionStorage.removeItem('returnPath');
        
        // Set flag to trigger search
        if (state.generalSearch || (state.filters && Object.keys(state.filters).length > 0)) {
          // We'll trigger search in another useEffect
          setSearched(false);
          // Show restored message
          setShowRestoredMessage(true);
        }
      } catch (error) {
        console.error('Error restoring search state:', error);
      }
    }
  }, [hasRestored]);
  
  // Trigger search after restoration
  useEffect(() => {
    if (hasRestored && !searched && (generalSearch || Object.keys(filters).length > 0)) {
      handleSearch().then(() => {
        // Scroll to search results after search completes
        setTimeout(() => {
          const resultsSection = document.querySelector('[data-search-results]');
          if (resultsSection) {
            resultsSection.scrollIntoView({ 
              behavior: 'smooth',
              block: 'start'
            });
          }
          // Hide restored message after scrolling
          setTimeout(() => {
            setShowRestoredMessage(false);
          }, 3000);
        }, 300);
      });
    }
  }, [hasRestored, generalSearch, filters]);

  const searchFields = [
    { key: 'Watts_W', label: 'Power (W)', type: 'number' },
    { key: 'ModuleEfficiency_percent', label: 'Efficiency (%)', type: 'number', step: '0.01' },
    { key: 'Length_mm', label: 'Length (mm)', type: 'number' },
    { key: 'Width_mm', label: 'Width (mm)', type: 'number' },
    { key: 'Thickness_mm', label: 'Thickness (mm)', type: 'number' },
    { key: 'Weight_kg', label: 'Weight (kg)', type: 'number', step: '0.1' },
    { key: 'NumberOfCells_count', label: 'Number of Cells', type: 'number' },
    { key: 'CableLength_mm', label: 'Cable Length (mm)', type: 'number' },
    { key: 'Vmp_V', label: 'Vmp (V)', type: 'number', step: '0.01' },
    { key: 'Imp_A', label: 'Imp (A)', type: 'number', step: '0.01' },
    { key: 'Voc_V', label: 'Voc (V)', type: 'number', step: '0.01' },
    { key: 'Isc_A', label: 'Isc (A)', type: 'number', step: '0.01' },
    { key: 'CellType', label: 'Cell Type', type: 'text' }
  ];

  const updateFilter = (key: string, field: keyof FilterConfig, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: {
        value: prev[key]?.value || '',
        tolerance: prev[key]?.tolerance || '',
        toleranceType: (prev[key]?.toleranceType as 'percent' | 'units') || 'percent',
        operator: prev[key]?.operator || 'equals',
        [field]: value
      }
    }));
  };

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    
    try {
      const searchParams: Record<string, any> = {};
      
      // Add general search parameter if provided
      if (generalSearch.trim()) {
        searchParams.search = generalSearch.trim();
      }
      
      Object.entries(filters).forEach(([key, config]) => {
        if (config.value) {
          searchParams[key] = config.value;
          if (config.operator && config.operator !== 'equals') {
            searchParams[`${key}_operator`] = config.operator;
          } else if (config.tolerance) {
            searchParams[`${key}_tol`] = config.tolerance;
            searchParams[`${key}_tol_type`] = config.toleranceType;
          }
        }
      });
      
      const response = await solarAPI.search(searchParams);
      setResults(response.panels);
      setSearchLimit(response.searchLimit || null);
      setSearched(true);
      
    } catch (err: any) {
      // Handle search limit exceeded error
      if (err.response?.status === 429) {
        setError(err.response.data.message || 'Search limit exceeded. Please login to continue searching.');
        setSearchLimit(err.response.data.searchLimit || {
          maxSearches: 15,
          remainingSearches: 0,
          limitReached: true
        });
      } else {
        setError(err.response?.data?.message || 'Search failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setFilters({});
    setGeneralSearch('');
    setResults([]);
    setSearched(false);
    setError('');
  };

  const handleRequestPanelSuccess = () => {
    setRequestPanelOpen(false);
    setError('');
  };

  const handleSupplierRequestSuccess = () => {
    setSupplierRequestOpen(false);
    setSupplierRequestSuccess(true);
  };

  const handleSupplierRequestClose = () => {
    setSupplierRequestSuccess(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-green-600 to-blue-600 text-white">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-white/20 blur-xl rounded-full"></div>
                <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                  <Zap className="h-12 w-12 text-yellow-300" />
                </div>
              </div>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-green-100">
              Find Solar Panels
              <br />
              <span className="text-4xl md:text-6xl">& Replacement Modules</span>
            </h1>
            <p className="text-xl md:text-2xl text-green-100 mb-8 max-w-4xl mx-auto leading-relaxed">
              Search our comprehensive database with precision matching.
              <br className="hidden md:block" />
              Connect directly with verified suppliers worldwide.
            </p>
            <div className="flex flex-wrap justify-center gap-6 mb-10">
              <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
                <CheckCircle className="h-5 w-5 text-green-300 mr-2" />
                <span className="text-sm font-medium">100% Free</span>
              </div>
              <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
                <Shield className="h-5 w-5 text-blue-300 mr-2" />
                <span className="text-sm font-medium">Verified Suppliers</span>
              </div>
              <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
                <Database className="h-5 w-5 text-purple-300 mr-2" />
                <span className="text-sm font-medium">Precision Matching</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row justify-center gap-4 text-black">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  onClick={() => document.getElementById('search-section')?.scrollIntoView({ behavior: 'smooth' })}
                  size="lg"
                  className="px-8 py-4 bg-white text-black hover:bg-green-50 w-full sm:w-auto text-lg font-semibold shadow-lg"
                  style={{
                    color: '#000000'
                  }}
                >
                  <Search className="h-5 w-5 mr-2 text-black" />
                  Start Searching
                </Button>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  onClick={() => setSupplierRequestOpen(true)}
                  variant="secondary"
                  size="lg"
                  className="px-8 py-4 bg-transparent border-2 border-white text-white hover:bg-white hover:text-green-600 w-full sm:w-auto text-lg font-semibold transition-all duration-300"
                >
                  <Building className="h-5 w-5 mr-2" />
                  Join as Supplier
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Our Platform?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The most comprehensive solar panel matching platform with advanced features
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center group"
            >
              <div className="relative mx-auto w-20 h-20 mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-blue-500 rounded-xl rotate-6 group-hover:rotate-12 transition-transform duration-300"></div>
                <div className="relative bg-white rounded-xl p-4 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                  <Database className="h-12 w-12 text-green-600 mx-auto" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Comprehensive Database</h3>
              <p className="text-gray-600 leading-relaxed">
                Thousands of solar panel specifications with detailed technical parameters and real-time availability.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center group"
            >
              <div className="relative mx-auto w-20 h-20 mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-500 rounded-xl rotate-6 group-hover:rotate-12 transition-transform duration-300"></div>
                <div className="relative bg-white rounded-xl p-4 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                  <Zap className="h-12 w-12 text-blue-600 mx-auto" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Precision Matching</h3>
              <p className="text-gray-600 leading-relaxed">
                Advanced filtering with customizable tolerance levels for exact specifications and replacement modules.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center group"
            >
              <div className="relative mx-auto w-20 h-20 mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-500 rounded-xl rotate-6 group-hover:rotate-12 transition-transform duration-300"></div>
                <div className="relative bg-white rounded-xl p-4 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                  <Users className="h-12 w-12 text-purple-600 mx-auto" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Direct Supplier Access</h3>
              <p className="text-gray-600 leading-relaxed">
                Connect instantly with verified suppliers worldwide. No middlemen, no extra costs, just direct connections.
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      
      {/* Search Restored Message */}
      {showRestoredMessage && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4"
        >
          <div className="flex items-center">
            <div className="bg-green-100 rounded-full p-1 mr-3">
              <Search className="h-4 w-4 text-green-600" />
            </div>
            <p className="text-green-800 font-medium">
              Your search has been restored and updated with inventory access!
            </p>
          </div>
        </motion.div>
      )}
      
        {/* Search Limit Information for Non-Authenticated Users */}
        {!isAuthenticated() && searchLimit && (
        <div className={`rounded-lg p-4 mb-6 ${
          searchLimit.limitReached 
            ? 'bg-red-50 border border-red-200' 
            : searchLimit.remainingSearches <= 3
            ? 'bg-yellow-50 border border-yellow-200'
            : 'bg-blue-50 border border-blue-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`font-medium ${
                searchLimit.limitReached 
                  ? 'text-red-800' 
                  : searchLimit.remainingSearches <= 3
                  ? 'text-yellow-800'
                  : 'text-blue-800'
              }`}>
                {searchLimit.limitReached 
                  ? 'Search Limit Reached' 
                  : 'Search Limit Notice'}
              </h3>
              <p className={`text-sm ${
                searchLimit.limitReached 
                  ? 'text-red-600' 
                  : searchLimit.remainingSearches <= 3
                  ? 'text-yellow-600'
                  : 'text-blue-600'
              }`}>
                {searchLimit.limitReached 
                  ? 'You have used all 15 free searches. Create a free account to continue.'
                  : `You have ${searchLimit.remainingSearches} searches remaining without an account.`}
              </p>
            </div>
            {(searchLimit.limitReached || searchLimit.remainingSearches <= 3) && (
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    // Store current search state in sessionStorage before redirecting
                    const searchState = {
                      generalSearch,
                      filters,
                      results,
                      searched
                    };
                    sessionStorage.setItem('searchState', JSON.stringify(searchState));
                    sessionStorage.setItem('returnPath', '/search');
                    navigate('/user/login');
                  }}
                  className="px-4 py-2 bg-[#63B56F] text-white text-sm font-medium rounded-md hover:bg-[#4A9B57] transition-colors"
                >
                  Login
                </button>
                <button
                  onClick={() => {
                    // Store current search state in sessionStorage before redirecting
                    const searchState = {
                      generalSearch,
                      filters,
                      results,
                      searched
                    };
                    sessionStorage.setItem('searchState', JSON.stringify(searchState));
                    sessionStorage.setItem('returnPath', '/search');
                    navigate('/user/signup');
                  }}
                  className="px-4 py-2 bg-white text-[#63B56F] text-sm font-medium rounded-md border border-[#63B56F] hover:bg-green-50 transition-colors"
                >
                  Sign Up Free
                </button>
              </div>
            )}
          </div>
        </div>
      )}

        {/* Search Section */}
        <div id="search-section" className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6 lg:p-8 mb-8 border border-gray-100">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="bg-gradient-to-r from-green-500 to-blue-500 rounded-full p-3">
                <Search className="h-8 w-8 text-white" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Search Solar Panels</h2>
            <p className="text-gray-600">Find panels by brand, SKU, or description. Use advanced filters for precise matching.</p>
          </div>

          {/* General Search Field */}
          <div className="mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-2xl mx-auto"
            >
              <label className="block text-lg font-semibold text-gray-800 mb-3 text-center">
                Quick Search
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Search className="h-6 w-6 text-gray-400" />
                </div>
                <Input
                  type="text"
                  placeholder="Search by brand name, SKU, model, or description..."
                  value={generalSearch}
                  onChange={(e) => setGeneralSearch(e.target.value)}
                  className="pl-12 py-4 text-lg w-full rounded-xl border-2 border-gray-200 focus:border-green-500 focus:ring-green-500 bg-gray-50 hover:bg-white transition-colors"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                />
              </div>
              <p className="text-sm text-gray-500 mt-2 text-center">
                Try searching for brands like "SunPower", "LG", "Trina" or specific SKU numbers Try searching for brands like "SunPower", "LG", "Trina" or specific SKU numbers and add first two letters of of the model number or product code ( CS For Canadian Solar )
              </p>
            </motion.div>
          </div>

          {/* Advanced Filters Section */}
          <div className="border-t border-gray-200 pt-8">
            <div className="text-center mb-6">
              <div className="flex justify-center mb-3">
                <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-full p-2">
                  <Filter className="h-6 w-6 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Advanced Filters</h3>
              <p className="text-gray-600 text-sm">Fine-tune your search with specific technical parameters</p>
            </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
          {searchFields.map((field, index) => (
            <motion.div
              key={field.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-gray-50 rounded-xl p-3 sm:p-4 space-y-3 hover:bg-gray-100 transition-colors duration-200"
            >
              <label className="block text-xs sm:text-sm font-semibold text-gray-800">
                {field.label}
              </label>
              <div className="flex flex-col space-y-2">
                <div className="flex-1">
                  {field.key === 'CellType' ? (
                    <>
                      <Input
                        list="cell-types"
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                        value={filters[field.key]?.value || ''}
                        onChange={(e) => updateFilter(field.key, 'value', e.target.value)}
                      />
                      <datalist id="cell-types">
                        {cellTypes.map((type) => (
                          <option key={type} value={type} className='text-gray-700'>{type}</option>
                        ))}
                      </datalist>
                    </>
                  ) : (
                    <Input
                      type={field.type === 'number' ? 'text' : field.type}
                      inputMode={field.type === 'number' ? 'decimal' : undefined}
                      pattern={field.type === 'number' ? '[0-9]*\\.?[0-9]*' : undefined}
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                      value={filters[field.key]?.value || ''}
                      onChange={(e) => updateFilter(field.key, 'value', e.target.value)}
                    />
                  )}
                </div>
                {field.type === 'number' && (
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                    <select
                      value={filters[field.key]?.operator || 'equals'}
                      onChange={(e) => updateFilter(field.key, 'operator', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#63B56F] text-sm"
                    >
                      <option value="equals">=</option>
                      <option value="gt">&gt;</option>
                      <option value="lt">&lt;</option>
                      <option value="gte">≥</option>
                      <option value="lte">≤</option>
                    </select>
                    {filters[field.key]?.operator === 'equals' && (
                      <div className="flex space-x-2">
                        <div className="flex-1">
                          <Input
                            type="text"
                            inputMode="decimal"
                            pattern="[0-9]*\.?[0-9]*"
                            placeholder="Tolerance"
                            value={filters[field.key]?.tolerance || ''}
                            onChange={(e) => updateFilter(field.key, 'tolerance', e.target.value)}
                            className="text-sm"
                          />
                        </div>
                        <select
                          value={filters[field.key]?.toleranceType || 'percent'}
                          onChange={(e) => updateFilter(field.key, 'toleranceType', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#63B56F] text-sm"
                        >
                          <option value="percent">%</option>
                          <option value="units">Units</option>
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
          </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4 mt-8">
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button 
                onClick={handleSearch} 
                loading={loading} 
                size="lg" 
                data-search-button="true"
                className="px-8 py-3 w-full sm:w-auto bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Search className="h-5 w-5 mr-3" />
                Search Panels
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button 
                onClick={resetFilters} 
                variant="secondary" 
                size="lg" 
                className="px-8 py-3 w-full sm:w-auto bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl border-2 border-gray-300 hover:border-gray-400 transition-all duration-300"
              >
                <RotateCcw className="h-5 w-5 mr-3" />
                Reset Filters
              </Button>
            </motion.div>
          </div>
        </div>
      
        {searched && results.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl shadow-lg border border-orange-200 p-6 sm:p-8 mb-8 text-center"
          >
            <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Search className="h-8 w-8 text-orange-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              No matching panels found
            </h3>
            <p className="text-gray-600 mb-6 text-base leading-relaxed max-w-2xl mx-auto">
              Don't worry! We're constantly expanding our database. Submit your panel specifications 
              and we'll help you find it while adding it for other users.
            </p>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button 
                onClick={() => setRequestPanelOpen(true)} 
                className="px-8 py-3 w-full sm:w-auto bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold rounded-xl shadow-lg"
              >
                <Plus className="h-5 w-5 mr-2" />
                Request Panel Addition
              </Button>
            </motion.div>
          </motion.div>
        )}
      
      {requestPanelOpen && (
        <PanelRequestForm
          onClose={() => setRequestPanelOpen(false)}
          onSuccess={handleRequestPanelSuccess}
        />
      )}

      {supplierRequestOpen && (
        <SupplierRequestForm
          onClose={() => setSupplierRequestOpen(false)}
          onSuccess={handleSupplierRequestSuccess}
        />
      )}

      {supplierRequestSuccess && (
        <SupplierRequestSuccess
          onClose={handleSupplierRequestClose}
        />
      )}
      
        <div data-search-results>
          {searched && <SearchResults results={results} loading={loading} searchState={{generalSearch, filters}} />}
        </div>
      </div>
    </div>
  );
};
